import { MaterialIcon } from '@/ui/material-icon';
import { Tabs, TabsList, TabsTrigger } from '@/ui/tabs';
import { parseAsInteger, useQueryStates } from 'nuqs';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSidebarDataQuery } from '../../hooks/useSidebarData';
import { useSidebarTab, type SidebarTab } from '../../hooks/useSidebarTab';
import { buildSidebarSections } from '../../utils/groupData.util';
import styles from './Sidebar.module.scss';
import SidebarContent from './SidebarContent';
import SidebarSkeleton from './SidebarSkeleton';

const pipelinePaginationParsers = {
  page: parseAsInteger.withDefault(1),
  pageSize: parseAsInteger.withDefault(10),
}

const Sidebar = () => {
  const navigate = useNavigate();
  const { tab: activeTab, setTab: setActiveTab } = useSidebarTab();
  const [, setPagination] = useQueryStates(pipelinePaginationParsers);
  const [searchQuery, setSearchQuery] = useState('');
  const { data, isLoading } = useSidebarDataQuery({ enabled: activeTab === 'assets' });
  const sections = useMemo(
    () => data ? buildSidebarSections(data[0], data[1], data[2]) : [],
    [data]
  );

  const showSkeleton = activeTab === 'assets' && (isLoading || sections.length === 0);

  const handleTabChange = (val: string) => {
    if (val !== 'pipelines') {
      void setPagination({ page: null, pageSize: null })
    }
    void setActiveTab(val as SidebarTab)
  }

  return (
    <aside className={styles.sidebar} aria-label="Application sidebar">
      <header className={styles.sidebarHeader}>
        <button className={styles.backButton} onClick={() => navigate(-1)}>
          <MaterialIcon name="chevron_left" className={styles.backIcon} />
          Back to Menu
        </button>
      </header>

      <Tabs
        value={activeTab}
        onValueChange={(val) => handleTabChange(val)}
        className={styles.tabs}
      >
        <TabsList className="w-full">
          <TabsTrigger value="assets" className="flex-1">Assets</TabsTrigger>
          <TabsTrigger value="pipelines" className="flex-1">Pipelines</TabsTrigger>
        </TabsList>

      </Tabs>

      {activeTab === 'assets' && (
        <>
          {showSkeleton ? (
            <SidebarSkeleton />
          ) : (
            <>
              <div className={styles.searchBar}>
                <label className={styles.searchInputWrapper}>
                  <MaterialIcon name="search" className={styles.searchIcon} />
                  <input
                    type="search"
                    placeholder="Search"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className={styles.searchInput}
                    aria-label="Search menu"
                  />
                </label>
              </div>
              <SidebarContent sections={sections} searchQuery={searchQuery} />
            </>
          )}
        </>
      )}

      <footer className={styles.footer}>
        <p className={styles.version}>Version 1.02.09</p>
      </footer>
    </aside>
  );
};

export default Sidebar;
